import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ColorField, ImageUpload } from "@/components/editor/fields";
import { api, formatErr } from "@/lib/api";
import { loadFonts, contrastRatio } from "@/lib/cardUtils";
import { AlertTriangle } from "lucide-react";

const COLORS = [
  ["background", "Sfondo"], ["surface", "Superfici"], ["title", "Titoli"], ["text", "Testi"],
  ["button", "Bottoni"], ["button_text", "Testo bottoni"], ["border", "Bordi"], ["accent", "Accento"],
];
const TPL_INFO = { minimal: "Essenziale, stile Apple", future: "Dark glass, futuristico", luxe: "Nero e oro, elegante", glass: "Vetro chiaro, tech", soft: "Pastello, morbido", bold: "Contrasto forte, grafico", corporate: "Strutturato, con copertina", creative: "Editoriale, asimmetrico" };

const ContrastWarning = ({ colors }) => {
  const checks = [["Titoli", colors.title, colors.background], ["Testi", colors.text, colors.background], ["Testo bottoni", colors.button_text, colors.button]];
  const bad = checks.filter(([, a, b]) => /^#[0-9A-F]{6}$/i.test(a) && /^#[0-9A-F]{6}$/i.test(b) && contrastRatio(a, b).ratio < 3);
  if (!bad.length) return null;
  return <p className="mt-2 flex gap-1.5 text-[11px] text-amber-700" data-testid="style-contrast-warning"><AlertTriangle className="h-3.5 w-3.5 shrink-0" />Contrasto basso per: {bad.map((b) => b[0]).join(", ")}. Il testo potrebbe risultare poco leggibile.</p>;
};

const Segmented = ({ value, options, onChange, testId }) => (
  <div className="grid gap-1 rounded-lg border bg-white p-1" style={{ gridTemplateColumns: `repeat(${options.length}, 1fr)` }}>
    {options.map(([k, l]) => (
      <button key={k} type="button" data-testid={`${testId}-${k}`} onClick={() => onChange(k)}
        className={`rounded-md px-2 py-1.5 text-xs transition-colors ${value === k ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>{l}</button>
    ))}
  </div>
);

const Label = ({ children }) => <div className="eyebrow mb-2">{children}</div>;

const TemplatePicker = ({ meta, style, update }) => (
  <div className="grid grid-cols-2 gap-2">
    {Object.entries(meta.templates).map(([k, tpl]) => (
      <button key={k} type="button" data-testid={`template-select-${k}`} onClick={() => update("style", { ...tpl, background_image: style.background_image || "", background_overlay: style.background_overlay ?? 40, background_blur: style.background_blur ?? 0 })}
        className={`overflow-hidden rounded-lg border text-left transition-all ${style.template === k ? "border-zinc-900 ring-1 ring-zinc-900" : "hover:border-zinc-400"}`}>
        <div className="flex h-16 items-end gap-1 p-2" style={{ background: tpl.colors.background }}>
          <span className="h-6 w-6" style={{ background: tpl.colors.button, borderRadius: tpl.avatar_shape === "circle" ? 99 : 3 }} />
          <span className="h-1.5 flex-1 rounded" style={{ background: tpl.colors.title, opacity: 0.7 }} />
        </div>
        <div className="bg-white p-2"><div className="text-xs font-medium capitalize">{k}</div><div className="text-[10px] text-zinc-500">{TPL_INFO[k]}</div></div>
      </button>
    ))}
  </div>
);

const Presets = ({ style, update }) => {
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get("/style-presets").then((r) => setList(r.data));
  useEffect(() => {
    load();
  }, []);
  const save = async () => {
    if (!name.trim()) return toast.error("Dai un nome allo stile");
    try {
      await api.post("/style-presets", { name, style });
      setName("");
      toast.success("Stile salvato: puoi riutilizzarlo su altre card");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input data-testid="preset-name-input" placeholder="Nome stile (es. Brand Rossi)" value={name} onChange={(e) => setName(e.target.value)} className="bg-white" />
        <Button variant="outline" onClick={save} data-testid="preset-save-button">Salva stile</Button>
      </div>
      {list.map((p) => (
        <div key={p.id} className="flex items-center gap-2 rounded-lg border bg-white px-3 py-2" data-testid={`preset-${p.id}`}>
          <div className="flex -space-x-1">{["background", "button", "accent"].map((k) => <span key={k} className="h-4 w-4 rounded-full border border-white" style={{ background: p.style.colors[k] }} />)}</div>
          <span className="flex-1 truncate text-sm">{p.name}</span>
          <button type="button" className="text-xs text-terra" data-testid={`preset-apply-${p.id}`} onClick={() => { update("style", { ...p.style }); toast.success(`Stile "${p.name}" applicato`); }}>Applica</button>
          <button type="button" className="text-zinc-400 hover:text-red-600" onClick={() => api.delete(`/style-presets/${p.id}`).then(load)}><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      ))}
    </div>
  );
};

const FontSelect = ({ value, fonts, onChange, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="bg-white" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>{fonts.map((f) => <SelectItem key={f} value={f}><span style={{ fontFamily: `"${f}"` }}>{f}</span></SelectItem>)}</SelectContent>
  </Select>
);

export const StylePanel = ({ card, update, meta }) => {
  const s = card.style;
  useEffect(() => {
    if (meta) loadFonts(meta.fonts);
  }, [meta]);
  if (!meta) return null;
  return (
    <div className="space-y-7" data-testid="editor-style-tab">
      <div><Label>Template</Label><TemplatePicker meta={meta} style={s} update={update} /><p className="mt-2 text-[11px] text-zinc-400">Cambiare template reimposta colori e font a quelli predefiniti del template.</p></div>
      <div className="space-y-3" data-testid="background-image-section">
        <Label>Immagine di sfondo</Label>
        <ImageUpload label="Sfondo della card (verticale, min. 1080×1920 consigliato)" aspect="wide" value={s.background_image} onChange={(v) => update("style.background_image", v)} testId="background-image-upload" />
        {s.background_image && (
          <>
            <div>
              <div className="flex justify-between text-[11px] text-zinc-500"><span>Velatura con il colore di sfondo</span><span data-testid="background-overlay-value">{s.background_overlay ?? 40}%</span></div>
              <Slider data-testid="background-overlay-slider" min={0} max={95} step={5} value={[s.background_overlay ?? 40]} onValueChange={([v]) => update("style.background_overlay", v)} />
            </div>
            <div>
              <div className="flex justify-between text-[11px] text-zinc-500"><span>Sfocatura</span><span>{s.background_blur ?? 0}px</span></div>
              <Slider data-testid="background-blur-slider" min={0} max={20} step={1} value={[s.background_blur ?? 0]} onValueChange={([v]) => update("style.background_blur", v)} />
            </div>
            <p className="text-[11px] text-zinc-400">Aumenta la velatura se i testi non si leggono bene sopra l'immagine.</p>
          </>
        )}
      </div>
      <div><Label>Colori</Label><div className="grid grid-cols-2 gap-2">{COLORS.map(([k, l]) => <ColorField key={k} label={l} value={s.colors[k]} onChange={(v) => update(`style.colors.${k}`, v)} testId={`${k === "accent" ? "accent" : k.replace("_", "-")}-color-picker`} />)}</div><ContrastWarning colors={s.colors} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Font titoli</Label><FontSelect value={s.font_heading} fonts={meta.fonts} onChange={(v) => update("style.font_heading", v)} testId="font-heading-select" /></div>
        <div><Label>Font testi</Label><FontSelect value={s.font_body} fonts={meta.fonts} onChange={(v) => update("style.font_body", v)} testId="font-body-select" /></div>
      </div>
      <div><Label>Avatar</Label><Segmented testId="avatar-shape" value={s.avatar_shape} onChange={(v) => update("style.avatar_shape", v)} options={[["circle", "Circolare"], ["rounded", "Arrotondato"], ["square", "Squadrato"]]} /></div>
      <div><Label>Bottoni</Label><Segmented testId="button-style" value={s.button_style} onChange={(v) => update("style.button_style", v)} options={[["filled", "Pieni"], ["outline", "Outline"], ["pill", "Arrotondati"]]} /></div>
      <div>
        <div className="flex justify-between"><Label>Border radius</Label><span className="font-mono-ui text-xs text-zinc-500" data-testid="radius-value">{s.radius}px</span></div>
        <Slider data-testid="radius-slider" min={0} max={40} step={1} value={[s.radius]} onValueChange={([v]) => update("style.radius", v)} />
      </div>
      <div><Label>Stili salvati</Label><Presets style={s} update={update} /></div>
    </div>
  );
};
